import {
  BomOptionsStepSeparateQuantity,
  DictionaryKeyDelimiter,
  QuantityType,
  QuestionType,
  RepresentationType,
  SelectionType,
  ThumbnailType,
} from './constants';
import { Int32MaxValue, QuantityOverflow } from '../utils/constants';
import {
  PARAMETER_BOMOPTIONSVALIDATIONOFF,
  PARAMETER_HASNOIMPRINTOPTIONS,
  PARAMETER_PACKAGESIGNATURE,
  PARAMETER_QUANTITYINCART,
  PARAMETER_STARTINGTOTALPRICE,
  PackageSignatureBomOptions,
  configuratorStep,
  promoStudioUITypeValue,
  validateResultMessage,
} from '../main/constants';
import {
  getBomPreviewColorCode,
  getColorCode,
  getCompany,
  getCurrentStep,
  getDivision,
  getFullPackageQuantity,
  getImprintAreaDict,
  getIsAdminEditReorder,
  getItemImprintAreaCode,
  getParameters,
  getSample,
  getShowAssortedColorsHelp,
  getWhetherIsForGooglePLA,
  getWhetherItemHasClickableColorSwatches,
  isInternalBrand,
} from '../main/selectors';
import { getMinimumQuantity, getRawPriceTable, getRawPriceTableValidation } from '../price/selectors';

import { InventoryStatusType } from '../inventories/constants';
import { createSelector } from 'reselect';
import { formatQuantity } from '../utils/formatter';
import { isValidQuantity } from '../utils/quantityValidation';

export const getQuestions = createSelector(
  (state) => state.questions,
  (questions) => {
    return questions.filter(
      (question) =>
        question.questionType !== QuestionType.LEAF || question.representationType !== RepresentationType.PARENT_CHILD
    );
  }
);

export const getShowSeparateQuantity = createSelector(
  getSample,
  (state) => state.questions,
  (isSample, questions) => {
    return (
      (questions.length === 0 || questions.every((question) => question.selectionType === SelectionType.SINGLE)) &&
      !isSample
    );
  }
);

export const getBomOptionsConfig = createSelector(
  getBomPreviewColorCode,
  getShowAssortedColorsHelp,
  getQuestions,
  getShowSeparateQuantity,
  (colorCodes, showAssortedColorsHelp, questions, showSeparateQuantity) => {
    const showPreview = colorCodes.length > 1;
    const showQuestionList = questions.length > 0;
    return { showAssortedColorsHelp, showPreview, showQuestionList, showSeparateQuantity };
  }
);

export const getUniqueOptions = createSelector(
  (state) => state.uniqueOptions,
  (_, code) => code,
  (uniqueOptions, code) => {
    return Object.keys(uniqueOptions).includes(code) ? uniqueOptions[code] : [];
  }
);

export const getAvailableUniqueOptions = createSelector(
  getColorCode,
  (state) => state.uniqueOptions,
  (state) => state.questions,
  (colorCodes, uniqueOptions, questions) => {
    let allOptions = [];

    if (uniqueOptions && colorCodes && colorCodes.length > 0) {
      colorCodes.forEach((code) => {
        const isMultiple = questions.find((x) => x.code === code)?.selectionType === SelectionType.MULTIPLE;
        if (isMultiple) {
          if (Object.keys(uniqueOptions).includes(code)) {
            allOptions = allOptions.concat(uniqueOptions[code]);
          }
        }
      });
    }

    return allOptions;
  }
);

export const getQuantity = createSelector(
  getSample,
  getShowSeparateQuantity,
  (state) => state.quantity,
  (state) => state.questions,
  (state) => state.uniqueOptions,
  (state) => state.childOptions,
  (isSample, needSeparateQuantity, quantity, questions, uniqueOptions, childOptions) => {
    if (isSample) {
      return 1;
    } else if (needSeparateQuantity) {
      return quantity;
    } else {
      const quantities = [];
      questions
        .filter(
          (question) =>
            question.representationType === RepresentationType.GENERAL &&
            question.selectionType === SelectionType.MULTIPLE
        )
        .forEach((question) => {
          const code = question.code;
          if (Object.keys(uniqueOptions).includes(code)) {
            uniqueOptions[code]
              .filter((option) => option.isSelected && option.quantity > 0)
              .forEach((option) => {
                quantities.push(option.quantity);
              });
          }
        });
      questions
        .filter(
          (question) =>
            question.questionType === QuestionType.LEAF &&
            question.representationType === RepresentationType.PARENT_CHILD
        )
        .forEach((question) => {
          const code = question.code;
          if (Object.keys(childOptions).includes(code)) {
            const subChildOptionDict = childOptions[code];
            Object.values(subChildOptionDict).forEach((options) => {
              options
                .filter((option) => option.isSelected && option.quantity > 0)
                .forEach((option) => {
                  quantities.push(option.quantity);
                });
            });
          }
        });
      return quantities.reduce((totalQuantity, currentQuantity) => {
        if (totalQuantity === QuantityOverflow) {
          return QuantityOverflow;
        } else if (totalQuantity + currentQuantity > Int32MaxValue) {
          return QuantityOverflow;
        } else {
          return totalQuantity + currentQuantity;
        }
      }, 0);
    }
  }
);

export const getChildQuestion = createSelector(
  (state) => state.questions,
  (state) => state.relations,
  (_, parentCode) => parentCode,
  (questions, relations, parentCode) => {
    const childCode = Object.keys(relations).includes(parentCode) ? relations[parentCode] : '';
    return questions.find((question) => question.code === childCode);
  }
);

export const getParentQuestion = createSelector(
  (state) => state.questions,
  (state) => state.relations,
  (_, childCode) => childCode,
  (questions, relations, childCode) => {
    let parentCode = '';
    for (const [key, value] of Object.entries(relations)) {
      if (value === childCode) {
        parentCode = key;
        break;
      }
    }
    return questions.find((question) => question.code === parentCode);
  }
);

export const getQuestion = createSelector(
  (state) => state.questions,
  (_, code) => code,
  (questions, code) => {
    return questions.find((question) => question.code === code);
  }
);

export const makeGetChildOptionsSelector = (childQuestionCode, key) => {
  return createSelector(
    (state) => state.childOptions,
    (childOptions) => {
      const subChildOptionDict = Object.keys(childOptions).includes(childQuestionCode)
        ? childOptions[childQuestionCode]
        : {};
      return Object.keys(subChildOptionDict).includes(key) ? subChildOptionDict[key] : [];
    }
  );
};

export const getBomOptionsForBomPreview = createSelector(
  (state) => state.uniqueOptions,
  (uniqueOptions) => {
    const result = [];
    Object.values(uniqueOptions).forEach((options) => {
      const index = options.findIndex((option) => option.isSelected);
      if (index >= 0) {
        result.push(options[index]);
      } else if (options.length > 0) {
        result.push(options[0]);
      }
    });
    return result;
  }
);

export const getBomOptionsForFullPreview = createSelector(
  getColorCode,
  (state) => state.uniqueOptions,
  (colorCodes, uniqueOptions) => {
    const entries = [];
    colorCodes.forEach((code) => {
      if (Object.keys(uniqueOptions).includes(code)) {
        const selectedOptions = uniqueOptions[code].filter((option) => option.isSelected);
        entries.push([code, selectedOptions]);
      }
    });
    return Object.fromEntries(entries);
  }
);

export const getBomOptionsForLitePreview = createSelector(
  getColorCode,
  (state) => state.uniqueOptions,
  (colorCodes, uniqueOptions) => {
    const entries = [];
    colorCodes.forEach((code) => {
      if (Object.keys(uniqueOptions).includes(code)) {
        let selectedOptions = uniqueOptions[code].filter((option) => option.isSelected);
        if (selectedOptions.length === 0) {
          if (uniqueOptions[code].length > 0) {
            selectedOptions = [uniqueOptions[code][0]];
          } else {
            selectedOptions = [];
          }
        }
        entries.push([code, selectedOptions]);
      }
    });
    return Object.fromEntries(entries);
  }
);

export const getFirstSelectedColorBomOption = createSelector(
  (state) => state.questions,
  (state) => state.uniqueOptions,
  (questions, uniqueOptions) => {
    let selectedColorBomOption = {};
    const colorQuestion = questions.find(
      (x) => x.thumbnailType === ThumbnailType.COLOR_SWATCH || x.thumbnailType === ThumbnailType.SCENE7_IMAGE
    );
    if (colorQuestion) {
      const colorCode = colorQuestion.code;
      if (Object.keys(uniqueOptions).includes(colorCode)) {
        selectedColorBomOption = uniqueOptions[colorCode][0];
      }
    }
    return selectedColorBomOption;
  }
);

export const getQuantityValidation = createSelector(
  getQuantity,
  getFullPackageQuantity,
  getMinimumQuantity,
  getRawPriceTable,
  getIsAdminEditReorder,
  isInternalBrand,
  (quantity, fullPackageQuantity, miniQuantity, priceTable, isAdminEditReorder, internalBrand) =>
    isAdminEditReorder
      ? {
          isValid: true,
          message: '',
        }
      : isValidQuantity(quantity, fullPackageQuantity, miniQuantity, priceTable, internalBrand)
);

export const getFinalBomOptionsSelection = createSelector(
  (state) => state.questions,
  (state) => state.relations,
  (state) => state.uniqueOptions,
  (state) => state.childOptions,
  getQuantity,
  (questions, relations, uniqueOptions, childOptions, quantity) => {
    const generalOptions = [];
    const pseudoParentChildQuestionOptionDictionaries = [];

    questions.forEach((question) => {
      let code;
      if (question.questionType === QuestionType.GENERAL) {
        code = question.code;
        if (Object.keys(uniqueOptions).includes(code)) {
          uniqueOptions[code]
            .filter((option) => option.isSelected)
            .forEach((option) => {
              generalOptions.push({
                componentId: option.componentId,
                quantity: option.quantity,
              });
            });
        }
      } else if (question.questionType === QuestionType.ROOT) {
        const keyParts = [];

        let tmpOptions;
        let tmpQuestion = question;
        while (tmpQuestion && tmpQuestion.questionType !== QuestionType.LEAF) {
          code = tmpQuestion.code;
          tmpOptions = Object.keys(uniqueOptions).includes(code) ? uniqueOptions[code] : [];
          keyParts.push(
            tmpOptions
              .filter((option) => option.isSelected)
              .map((option) => {
                return {
                  key: question.isRealParentChildQuestion ? option.codeValue : option.componentId,
                  quantity: option.quantity,
                };
              })
          );

          const childCode = Object.keys(relations).includes(code) ? relations[code] : '';
          tmpQuestion = questions.find((question) => question.code === childCode);
        }
        console.log('Key Parts:');
        console.log(keyParts);

        const keyHelpers = keyParts.some((parts) => parts.length === 0)
          ? []
          : keyParts.reduce((keyHelpersAccumulator, currentKeyParts) => {
              const tmpKeyHelpers = [];
              keyHelpersAccumulator.forEach((keyHelper) =>
                currentKeyParts.forEach((keyPart) =>
                  tmpKeyHelpers.push({
                    key: keyHelper.key.concat(DictionaryKeyDelimiter, keyPart.key),
                    quantity: keyPart.quantity > 0 ? keyPart.quantity : keyHelper.quantity,
                  })
                )
              );
              return tmpKeyHelpers;
            });
        console.log('Key Helpers:');
        console.log(keyHelpers);

        const leafCode = tmpQuestion.code;
        const leafOptionDict = Object.keys(childOptions).includes(leafCode) ? childOptions[leafCode] : {};
        const pseudoParentChildQuestionOptionEntries = [];
        keyHelpers.forEach((keyHelper) => {
          if (Object.keys(leafOptionDict).includes(keyHelper.key)) {
            if (question.representationType === RepresentationType.GENERAL) {
              tmpOptions = [];
              const leafOptions = leafOptionDict[keyHelper.key];
              const leafUniqueOptions = Object.keys(uniqueOptions).includes(leafCode) ? uniqueOptions[leafCode] : [];
              leafUniqueOptions
                .filter((option) => option.isSelected)
                .forEach((selectedOption) => {
                  const leafOption = leafOptions.find((option) => option.codeValue === selectedOption.codeValue);
                  if (leafOption) {
                    tmpOptions.push({
                      componentId: leafOption.componentId,
                      quantity: selectedOption.quantity > 0 ? selectedOption.quantity : keyHelper.quantity,
                    });
                  }
                });
            } else {
              tmpOptions = leafOptionDict[keyHelper.key]
                .filter((option) => option.isSelected)
                .map((option) => {
                  return {
                    componentId: option.componentId,
                    quantity: option.quantity,
                  };
                });
            }

            if (question.isRealParentChildQuestion) {
              generalOptions.push(...tmpOptions);
            } else {
              pseudoParentChildQuestionOptionEntries.push([keyHelper.key, tmpOptions]);
            }
          }
        });
        if (pseudoParentChildQuestionOptionEntries.length > 0) {
          pseudoParentChildQuestionOptionDictionaries.push(Object.fromEntries(pseudoParentChildQuestionOptionEntries));
        }
      }
    });

    const finalSelection = { generalOptions, pseudoParentChildQuestionOptionDictionaries, quantity };
    console.log('Final BomOptions Selection:');
    console.log(finalSelection);
    return finalSelection;
  }
);

export const getSelectedComponentId = createSelector(getFinalBomOptionsSelection, (finalBomOptionsSelection) => {
  let componentId =
    finalBomOptionsSelection.generalOptions.length > 0 && finalBomOptionsSelection.generalOptions[0].componentId;
  return componentId;
});

export const getItemInventoryDetails = createSelector(
  (state) => state.questions,
  (state) => state.uniqueOptions,
  (state) => state.childOptions,
  (state) => state.specs,
  (state) => state.inventories,
  getFinalBomOptionsSelection,
  (questions, uniqueOptions, childOptions, specs, inventories, bomSelection) => {
    const options = [];
    questions
      .filter((question) => question.questionType === QuestionType.GENERAL)
      .forEach((question) => {
        const { code } = question;
        if (Object.keys(uniqueOptions).includes(code)) {
          options.push(...uniqueOptions[code]);
        }
      });
    questions
      .filter((question) => question.questionType === QuestionType.LEAF)
      .forEach((question) => {
        const { code } = question;
        if (Object.keys(childOptions).includes(code)) {
          options.push(...Object.values(childOptions[code]).flat());
        }
      });

    const areAllBomOptionsOutOfStock = options.length > 0 && options.every((option) => option.isOutOfStock);
    const areAllBomOptionsSoldOut = options.length > 0 && options.every((option) => option.isSoldOut);
    const hasNoBomOptions = options.length === 0;

    let isItemWithNoBomOptionsOutOfStock = false;
    let isItemWithNoBomOptionsSoldOut = false;
    let inventoryMessage = '';
    if (hasNoBomOptions) {
      const { checkInventory, itemId } = specs;
      const itemInventory = inventories.find((inventory) => inventory.componentId === itemId);
      if (!!itemInventory) {
        isItemWithNoBomOptionsOutOfStock = itemInventory.inventoryStatus === InventoryStatusType.TemporarilyOutOfStock;
        isItemWithNoBomOptionsSoldOut = itemInventory.inventoryStatus === InventoryStatusType.ReallyOutOfStock;
        if (
          !isItemWithNoBomOptionsOutOfStock &&
          !isItemWithNoBomOptionsSoldOut &&
          checkInventory &&
          itemInventory.availableQuantity !== null &&
          itemInventory.availableQuantity > 0
        ) {
          inventoryMessage = `${formatQuantity(itemInventory.availableQuantity)} Available`;
        }
      }
    }
    const isOutOfStock = areAllBomOptionsOutOfStock || isItemWithNoBomOptionsOutOfStock;
    const isSoldOut = areAllBomOptionsSoldOut || isItemWithNoBomOptionsSoldOut;

    const { allowBackorders, promoStudioUIType, isTecSysItem } = specs;
    const { generalOptions } = bomSelection;
    const isLite = promoStudioUIType === promoStudioUITypeValue.LiteUI;
    let isSelectedBomOptionOutOfStock = false;
    if (!hasNoBomOptions && generalOptions.length > 0) {
      const selectedOption = options.find((option) => option.componentId === generalOptions[0].componentId);
      if (!!selectedOption) {
        isSelectedBomOptionOutOfStock = selectedOption.isOutOfStock;
      }
    }
    const skipInventoryValidationForLiteTecSysItemIfAllowBackorders =
      (isOutOfStock || isSelectedBomOptionOutOfStock) && isLite && isTecSysItem && allowBackorders;

    return {
      allowBackorders,
      areAllBomOptionsOutOfStock,
      areAllBomOptionsSoldOut,
      hasNoBomOptions,
      isItemWithNoBomOptionsOutOfStock,
      isItemWithNoBomOptionsSoldOut,
      isOutOfStock,
      isSoldOut,
      inventoryMessage,
      skipInventoryValidationForLiteTecSysItemIfAllowBackorders,
    };
  }
);

export const getTecSysItemInventoryValidation = createSelector(
  (state) => state.specs,
  (state) => state.inventories,
  getFinalBomOptionsSelection,
  getItemInventoryDetails,
  (specs, inventories, bomSelection, inventoryDetails) => {
    let validation = { isValid: true, message: '' };
    const { promoStudioUIType, isTecSysItem, itemId } = specs;
    const { generalOptions, quantity } = bomSelection;
    const { hasNoBomOptions, skipInventoryValidationForLiteTecSysItemIfAllowBackorders } = inventoryDetails;

    if (promoStudioUIType !== promoStudioUITypeValue.LiteUI || !isTecSysItem) return validation;
    if (inventories.length === 0 || (!hasNoBomOptions && generalOptions.length === 0) || quantity === 0)
      return validation;
    if (skipInventoryValidationForLiteTecSysItemIfAllowBackorders) return validation;

    const componentId = hasNoBomOptions ? itemId : generalOptions[0].componentId;
    const targetInventory = inventories.find((inventory) => inventory && inventory.componentId === componentId);
    if (
      !!targetInventory &&
      targetInventory.availableQuantity !== null &&
      quantity > targetInventory.availableQuantity
    ) {
      validation = {
        isValid: false,
        message: 'Order quantity cannot exceed available quantity - please update order.',
      };
    }

    return validation;
  }
);

export const getBomOptionsValidation = createSelector(
  getSample,
  getQuestions,
  (state) => state.uniqueOptions,
  getMinimumQuantity,
  getQuantity,
  getTecSysItemInventoryValidation,
  getQuantityValidation,
  getCurrentStep,
  (
    isSample,
    questions,
    uniqueOptions,
    miniQuantity,
    quantity,
    inventoryValidation,
    quantityValidation,
    currentStep
  ) => {
    let selectionValidation = { isValid: true, message: '' };
    for (const question of questions) {
      const code = question.code;
      if (Object.keys(uniqueOptions).includes(code)) {
        const options = uniqueOptions[code];
        if (options.findIndex((option) => option.isSelected) < 0) {
          if (currentStep === configuratorStep.imprintarea) {
            selectionValidation = {
              isValid: false,
              message: validateResultMessage.bomOptionSoldOut,
            };
            break;
          } else {
            const questionText =
              isSample && question.sampleQuestionText
                ? question.sampleQuestionText
                : options.length > 1
                ? question.questionText
                : question.questionLabel;
            selectionValidation = {
              isValid: false,
              message: `Please make a selection in the option labeled "${questionText}".`,
            };
            break;
          }
        }
      }
    }
    if (!selectionValidation.isValid) return selectionValidation;

    if (quantity >= 0 && quantity < miniQuantity) {
      return {
        isValid: false,
        message: `Your order must have a minimum total quantity of ${formatQuantity(
          miniQuantity
        )} to continue. Please enter a valid quantity.`,
      };
    }

    if (!inventoryValidation.isValid) return inventoryValidation;

    return quantityValidation;
  }
);

export const getSizeChart = createSelector(
  (state) => state.sizeChart,
  (sizeChart) => sizeChart || {}
);

export const getMatchTrimColor = createSelector(
  (state) => state.uniqueOptions,
  (uniqueOptions) => {
    const TrimColorCode = 'TRM';
    let result = { hasTrimColor: false, colorReference: undefined };
    if (
      Object.keys(uniqueOptions).includes(TrimColorCode) &&
      uniqueOptions[TrimColorCode].length > 0 &&
      uniqueOptions[TrimColorCode].some((option) => option.isSelected)
    ) {
      const option = uniqueOptions[TrimColorCode].find((option) => option.isSelected);
      result = { hasTrimColor: true, colorReference: option.colorReference };
    }
    return result;
  }
);

export const getIfCurrentBrandIsFittedForFutureShip = createSelector(getCompany, getDivision, (company, division) => {
  const fittedForFutureShipBrands = ['APL-APL', 'CAN-CAN', 'BWC-BWC', 'USD-USD', 'THY-COR'];
  return fittedForFutureShipBrands.includes(`${company.toUpperCase()}-${division.toUpperCase()}`);
});

export const getAllQuestions = (state) => state.questions;

export const getAllUniqueOptions = (state) => state.uniqueOptions;

export const getQuantityChangeStep = createSelector(
  getFullPackageQuantity,
  getMinimumQuantity,
  getRawPriceTable,
  getRawPriceTableValidation,
  (fullPackageQuantity, miniQuantity, priceTable, priceTableValidation) => {
    const defaultChangeStep = 1;

    if (!priceTableValidation.isValid) return defaultChangeStep;

    const lockQtyFlag = priceTable[0].lockQtyFlag.trim().toUpperCase();
    const priceCode = priceTable[0].priceCode.trim().toUpperCase();

    if (lockQtyFlag === 'Y') {
      return miniQuantity;
    } else if (lockQtyFlag === 'C' && fullPackageQuantity > 0) {
      return fullPackageQuantity;
    } else if (priceCode === 'M' || priceCode === 'C') {
      return miniQuantity;
    } else {
      return defaultChangeStep;
    }
  }
);

const getUpdatedPreselections = createSelector(
  (state) => state.questions,
  (state) => state.relations,
  (state) => state.uniqueOptions,
  (state) => state.childOptions,
  getQuantity,
  (questions, relationDict, uniqueOptionDict, childOptionDict, totalQuantity) => {
    const singleSelectionDict = Object.fromEntries(
      questions
        .filter(
          (question) =>
            question.selectionType === SelectionType.SINGLE &&
            Object.keys(uniqueOptionDict).includes(question.code) &&
            uniqueOptionDict[question.code].some((option) => option.isSelected)
        )
        .map((question) =>
          uniqueOptionDict[question.code]
            .filter((option) => option.isSelected)
            .map((option) => [option.code, option.codeValue])
        )
        .flat()
    );

    const multipleSelections = [
      ...questions
        .filter(
          (question) =>
            question.representationType === RepresentationType.GENERAL &&
            question.selectionType === SelectionType.MULTIPLE &&
            Object.keys(uniqueOptionDict).includes(question.code) &&
            uniqueOptionDict[question.code].some((option) => option.isSelected)
        )
        .map((question) =>
          uniqueOptionDict[question.code]
            .filter((option) => option.isSelected)
            .map((option) => ({
              optionDictionary: {
                [option.code]: option.codeValue,
              },
              quantity: option.quantity,
            }))
        )
        .flat(),
      ...questions
        .filter(
          (question) =>
            question.questionType === QuestionType.LEAF &&
            question.representationType === RepresentationType.PARENT_CHILD &&
            question.selectionType === SelectionType.MULTIPLE &&
            Object.keys(childOptionDict).includes(question.code) &&
            Object.entries(childOptionDict[question.code]).length > 0
        )
        .map((question) => {
          const [parentCode, childCode] = Object.entries(relationDict).find(
            ([parentCode, childCode]) => childCode === question.code
          );

          return Object.entries(childOptionDict[question.code])
            .map(([parentCodeValue, options]) =>
              options
                .filter((option) => option.isSelected)
                .map((option) => ({
                  optionDictionary: {
                    [parentCode]: parentCodeValue,
                    [option.code]: option.codeValue,
                  },
                  quantity: option.quantity,
                }))
            )
            .flat();
        })
        .flat(),
    ];

    multipleSelections.forEach(
      (selection) => (selection.optionDictionary = Object.assign(selection.optionDictionary, singleSelectionDict))
    );

    return multipleSelections.length > 0
      ? multipleSelections
      : Object.entries(singleSelectionDict).length > 0
      ? [
          {
            optionDictionary: singleSelectionDict,
            quantity: totalQuantity,
          },
        ]
      : [];
  }
);

export const getUpdatePreselectionRequest = createSelector(
  getParameters,
  getUpdatedPreselections,
  isInternalBrand,
  (state) => state.specs,
  (parameters, preselections, internalBrand, specs) => {
    const { eTag, preconfigurationId, token } = parameters;
    const { allowPreselectBom } = specs;

    return !!eTag &&
      !!preconfigurationId &&
      !!token &&
      !!preselections &&
      preselections.length > 0 &&
      !internalBrand &&
      allowPreselectBom
      ? {
          lastUpdateTs: eTag,
          preconfigurationId,
          preselections,
          token,
        }
      : null;
  }
);

export const getChildOptionDict = createSelector(
  (state) => state.childOptions,
  (_, childCode) => childCode,
  (childOptions, childCode) => childOptions[childCode] || {}
);

export const getQuantityConfig = createSelector(
  getRawPriceTable,
  getFullPackageQuantity,
  getMinimumQuantity,
  (priceTable, fullPackageQuantity, minimumQuantity) => {
    let minimumDivisor = 0;
    let type = QuantityType.Common;

    if (!priceTable || priceTable.length === 0) {
      return {
        minimumDivisor,
        type,
      };
    }

    const lockQtyFlag = priceTable[0].lockQtyFlag.trim().toUpperCase();
    const priceCode = priceTable[0].priceCode.trim().toUpperCase();

    switch (lockQtyFlag) {
      case 'Y':
        minimumDivisor = minimumQuantity;
        type = QuantityType.Locked;
        break;
      case 'E':
        minimumDivisor = 0;
        type = QuantityType.Exact;
        break;
      case 'C':
        if (fullPackageQuantity > 0) {
          minimumDivisor = fullPackageQuantity;
          type = QuantityType.Locked;
        }
        break;
      default:
        if (priceCode === 'M' || priceCode === 'C') {
          minimumDivisor = minimumQuantity;
          type = QuantityType.Locked;
        }
        break;
    }

    return {
      minimumDivisor,
      type,
    };
  }
);

export const getMultiSelectQuantityDetails = createSelector(
  getQuestions,
  (state) => state.uniqueOptions,
  (state) => state.relations,
  (state) => state.childOptions,
  (questions, uniqueOptions, relations, childOptions) => {
    const details = Object.fromEntries(
      questions
        .filter((question) => question.selectionType === SelectionType.MULTIPLE)
        .map((question) =>
          uniqueOptions[question.code]
            .filter((option) => option.isSelected)
            .map((option) => [
              option.codeValue,
              question.representationType === RepresentationType.GENERAL
                ? option.quantity
                : childOptions[relations[question.code]][option.codeValue]
                    .filter((option) => option.isSelected)
                    .map((option) => option.quantity)
                    .reduce((totalQty, currentQty) => totalQty + currentQty, 0),
            ])
        )
        .flat()
    );

    console.log('Invoke getMultiSelectQuantityDetails():');
    console.log(details);
    return details;
  }
);

export const getBomOptionsErrors = createSelector(
  getQuestions,
  (state) => state.uniqueOptions,
  getMinimumQuantity,
  getMultiSelectQuantityDetails,
  getQuantity,
  getQuantityConfig,
  getQuantityValidation,
  (
    questions,
    uniqueOptions,
    miniQuantity,
    multiSelectQuantityDetails,
    quantity,
    quantityConfig,
    quantityValidation
  ) => {
    let errors = {};
    let returnQuantityError = true;

    questions.forEach((question) => {
      let { code, questionLabel, selectionType } = question;
      const options = uniqueOptions[code] || [];
      if (options.length > 0 && options.every((option) => !option.isSelected)) {
        questionLabel = !!questionLabel ? questionLabel.toLowerCase().trim() : '';
        if (questionLabel.endsWith(':')) questionLabel = questionLabel.slice(0, questionLabel.length - 1);
        errors[code] = `Please select the ${questionLabel}`;

        if (selectionType === SelectionType.MULTIPLE) {
          returnQuantityError = false;
        }
      }
    });

    if (returnQuantityError) {
      const hasOptionsWithoutQuantityInput = Object.values(multiSelectQuantityDetails).some((qty) => qty === 0);
      const enterQuantityMode =
        quantityConfig.type === QuantityType.Common ||
        questions.some((question) => question.selectionType === SelectionType.MULTIPLE);
      if (quantity === 0 || hasOptionsWithoutQuantityInput) {
        errors['quantity'] = `Please ${enterQuantityMode ? 'enter' : 'select'} your quantity number`;
      } else if (quantity > 0 && quantity < miniQuantity) {
        errors['quantity'] = 'Your total quantity does not meet the minimum requirement';
      } else if (!quantityValidation.isValid) {
        errors['quantity'] = quantityValidation.message;
      }
    }

    console.log('Bom Options Errors:');
    console.log(errors);
    return errors;
  }
);

export const getWhetherCurrentContextIsBomOptionsPackage = createSelector(
  (state) => state.parameters,
  (parameters) =>
    Object.keys(parameters).includes(PARAMETER_PACKAGESIGNATURE) &&
    parameters[PARAMETER_PACKAGESIGNATURE] === PackageSignatureBomOptions
);

export const getWhetherCurrentBomOptionGroupCanChangeProductPreviewColor = createSelector(
  getWhetherItemHasClickableColorSwatches,
  getBomPreviewColorCode,
  (_, code) => code,
  (hasClickableColorSwatches, colorCodes, code) => {
    return (
      hasClickableColorSwatches &&
      !!colorCodes &&
      !!code &&
      colorCodes.length === 1 &&
      colorCodes[0].toUpperCase() === code.toUpperCase()
    );
  }
);

export const getWhetherNeedToDispatchBomColorChangeEvent = createSelector(
  getWhetherCurrentContextIsBomOptionsPackage,
  getWhetherCurrentBomOptionGroupCanChangeProductPreviewColor,
  (isBomOptionsPackageContext, canChangeProductPreviewColor) => {
    return isBomOptionsPackageContext && canChangeProductPreviewColor;
  }
);

export const getAllowDesignLaterForBomOptions = createSelector(
  getWhetherCurrentContextIsBomOptionsPackage,
  (state) => state.specs,
  (isBomOptionsPackageContext, specs) =>
    isBomOptionsPackageContext && specs.isDesignLater && !specs.isOutOfStock && !specs.quotedUponRequest
);

export const getQuantityInCart = createSelector(
  (state) => state.parameters,
  (parameters) =>
    Object.keys(parameters).includes(PARAMETER_QUANTITYINCART) ? Number(parameters[PARAMETER_QUANTITYINCART]) : 0
);

export const getStepNumberForBomOptions = createSelector(
  getQuestions,
  getShowSeparateQuantity,
  (state) => state.uniqueOptions,
  (_, step) => step,
  (questions, showSeparateQuantity, uniqueOptions, step) => {
    const steps = [];

    questions.forEach((question) => {
      const { code, selectionType } = question;
      steps.push(code);

      if (selectionType === SelectionType.MULTIPLE && uniqueOptions[code].some((option) => option.isSelected)) {
        steps.push(`${code}-quantity`);
      }
    });

    if (showSeparateQuantity) {
      steps.push(BomOptionsStepSeparateQuantity);
    }

    const index = steps.indexOf(step);
    return index >= 0 ? index + 1 : index;
  }
);

export const getBomOptionsValidationSummary = createSelector(
  getQuestions,
  (state) => state.uniqueOptions,
  getMinimumQuantity,
  getMultiSelectQuantityDetails,
  getQuantity,
  getQuantityConfig,
  getQuantityValidation,
  (
    questions,
    uniqueOptions,
    miniQuantity,
    multiSelectQuantityDetails,
    quantity,
    quantityConfig,
    quantityValidation
  ) => {
    let questionLabels = [];
    let returnQuantityError = true;

    questions.forEach((question) => {
      let { code, questionLabel, selectionType } = question;
      const options = uniqueOptions[code] || [];
      if (options.length > 0 && options.every((option) => !option.isSelected)) {
        questionLabel = !!questionLabel ? questionLabel.toLowerCase().trim() : '';
        if (questionLabel.endsWith(':')) questionLabel = questionLabel.slice(0, questionLabel.length - 1);
        questionLabels.push(questionLabel);

        if (selectionType === SelectionType.MULTIPLE) {
          returnQuantityError = false;
        }
      }
    });

    let alertQuantityLabel = '';
    let tooltipQuantityLabel = '';
    const alertLabels = [...questionLabels];
    const tooltipLabels = [...questionLabels];
    const hasOptionsWithoutQuantityInput = Object.values(multiSelectQuantityDetails).some((qty) => qty === 0);
    const enterQuantityMode =
      quantityConfig.type === QuantityType.Common ||
      questions.some((question) => question.selectionType === SelectionType.MULTIPLE);
    if (returnQuantityError) {
      if (!quantityValidation.isValid) {
        if (enterQuantityMode) {
          if (quantityConfig.type === QuantityType.Common || quantity < miniQuantity) {
            alertQuantityLabel = `add minimum quantity requirement of ${formatQuantity(miniQuantity)}`;
            tooltipQuantityLabel = 'minimum quantity';
          } else {
            alertQuantityLabel = 'add correct quantity';
            tooltipQuantityLabel = 'correct quantity';
          }
        } else {
          alertQuantityLabel = `minimum quantity requirement of ${formatQuantity(miniQuantity)}`;
          tooltipLabels.push('quantity');
        }
      } else {
        if (hasOptionsWithoutQuantityInput) {
          alertQuantityLabel = tooltipQuantityLabel = `${enterQuantityMode ? 'enter' : 'select'} your quantity number`;
        }
      }
    }

    let alert;
    if (alertLabels.length === 0) {
      if (quantity === 0 || hasOptionsWithoutQuantityInput) {
        alert = `Please ${enterQuantityMode ? 'enter' : 'select'} your quantity number`;
      } else if (quantity > 0 && quantity < miniQuantity) {
        alert = 'Your total quantity does not meet the minimum requirement';
      } else if (!quantityValidation.isValid) {
        alert = quantityValidation.message;
      } else {
        alert = '';
      }
    } else {
      if (!!alertQuantityLabel) {
        alertLabels.push(alertQuantityLabel);
      }
      alert = `Select your ${alertLabels.reduce((accumulator, currentValue, index, array) =>
        array.length === 1
          ? currentValue
          : index === array.length - 1
          ? accumulator + ' and ' + currentValue
          : accumulator + ', ' + currentValue
      )}`;
    }

    const tooltip = (
      <>
        {tooltipLabels.length > 0 && (
          <>
            Select <span>{tooltipLabels.join(' & ')}</span>
          </>
        )}
        {tooltipLabels.length > 0 && !!tooltipQuantityLabel && ' and '}
        {!!tooltipQuantityLabel &&
          (!quantityValidation.isValid ? (
            <>
              add <span>{tooltipQuantityLabel}</span>
            </>
          ) : (
            tooltipQuantityLabel
          ))}
        {' above to start personalizing'}
      </>
    );

    return { alert, tooltip };
  }
);

export const getWhetherShowStartingTotalPriceForGooglePLA = createSelector(
  getWhetherIsForGooglePLA,
  getQuestions,
  (state) => state.uniqueOptions,
  getQuantity,
  (isForGooglePLA, questions, uniqueOptions, quantity) =>
    isForGooglePLA &&
    questions.every((question) => {
      const options = uniqueOptions[question.code] || [];
      return options.length === 1 || options.every((option) => !option.isSelected);
    }) &&
    quantity === 0
);

export const getStartingTotalPriceForGooglePLA = createSelector(
  (state) => state.parameters,
  (parameters) =>
    Object.keys(parameters).includes(PARAMETER_STARTINGTOTALPRICE)
      ? Number(parameters[PARAMETER_STARTINGTOTALPRICE])
      : 0.0
);

export const getHasNoImprintOptionsFlagFromParameters = createSelector(
  (state) => state.parameters,
  (parameters) => {
    const hasNoImprintOptionsFlag = parameters[PARAMETER_HASNOIMPRINTOPTIONS];
    return typeof hasNoImprintOptionsFlag === 'boolean'
      ? hasNoImprintOptionsFlag
      : typeof hasNoImprintOptionsFlag === 'string'
      ? hasNoImprintOptionsFlag.toLowerCase().trim() === 'true'
      : typeof hasNoImprintOptionsFlag === 'number'
      ? hasNoImprintOptionsFlag === 1
      : Boolean(hasNoImprintOptionsFlag);
  }
);

export const getWhetherHasBomOptionsSelected = createSelector(
  getQuestions,
  (state) => state.uniqueOptions,
  getQuantity,
  (questions, uniqueOptions, quantity) =>
    questions
      .map((question) => uniqueOptions[question.code] || [])
      .flat()
      .some((option) => option.isSelected) || quantity > 0
);

export const getMultiSelectableBomOptions = createSelector(
  getQuestions,
  (state) => state.uniqueOptions,
  (questions, uniqueOptions) =>
    questions
      .filter((question) => question.selectionType === SelectionType.MULTIPLE)
      .map((question) => uniqueOptions[question.code])
      .flat()
);

export const getWhetherItemIsNonImprinted = createSelector(
  getHasNoImprintOptionsFlagFromParameters,
  getItemImprintAreaCode,
  getImprintAreaDict,
  (hasNoImprintOptions, imprintAreaCode, imprintAreaDict) =>
    hasNoImprintOptions || (!imprintAreaCode && Object.entries(imprintAreaDict).length === 0)
);

export const getBomOptionsValidationOffFlagFromParameters = createSelector(
  (state) => state.parameters,
  (parameters) => {
    const validationOffFlag = parameters[PARAMETER_BOMOPTIONSVALIDATIONOFF];
    return typeof validationOffFlag === 'boolean'
      ? validationOffFlag
      : typeof validationOffFlag === 'string'
      ? validationOffFlag.toLowerCase().trim() === 'true' || validationOffFlag.trim() === '1'
      : typeof validationOffFlag === 'number'
      ? validationOffFlag === 1
      : Boolean(validationOffFlag);
  }
);
